import {
  ref, toValue, watch, type MaybeRefOrGetter, type Ref,
} from 'vue';
import { useLocalStorage } from './useLocalStorage';
import type { Column } from '@apparatix/types';

type Options = {
  visibleItemsKey: string;
  columnOrderKey: string;
  ref: Ref<any>;
  defaultColumns: MaybeRefOrGetter<Column[]>;
}

export const useColumnConfig = (options: Options) => {
  const { getItem } = useLocalStorage();
  const columnOrder = ref<Column[]>([]);
  const visibleColumns = ref<string[]>([]);

  const savedVisible = getItem<string[]>(options.visibleItemsKey);
  const savedOrder = getItem<Column[]>(options.columnOrderKey);

  // True once syncWithDefaults has run at least once — distinguishes the initial load (apply the
  // localStorage snapshot) from every later re-fire.
  let hasInitialized = false;

  // Re-runs whenever defaultColumns changes (e.g. once an async options fetch resolves, or a
  // later refetch returns a new array for the same/updated columns) so newly-available columns
  // and up-to-date labels get merged in. For static array callers this fires exactly once, since
  // the array reference never changes.
  //
  // Only the FIRST run applies the localStorage snapshot (savedVisible/savedOrder) as the
  // baseline. Every later run baselines off columnOrder.value/visibleColumns.value instead — the
  // live, possibly user-edited-this-session state — so a refetch (e.g. Refresh re-loading
  // componentOptions) never reverts a column show/hide the user just made back to what was saved
  // last time they used Configure Columns.
  const syncWithDefaults = (defaultColumns: Column[]) => {
    const baseOrder = hasInitialized ? columnOrder.value : (savedOrder ?? defaultColumns);
    const baseVisible = hasInitialized
      ? visibleColumns.value
      : (savedVisible ?? defaultColumns.filter(col => col.visible).map(col => col.field));

    // Columns present in defaultColumns but not yet in the baseline — either genuinely new
    // (first time this key has appeared) or, on the first run, columns added since the user last
    // saved their column order.
    const missingColumns = defaultColumns.filter(col =>
      !baseOrder.some(existing => existing.field === col.field),
    );

    const visibleNewFields = missingColumns.filter(col => col.visible).map(col => col.field);
    visibleColumns.value = [...baseVisible, ...visibleNewFields];

    // Re-map every known column's header from the current defaults so a stale label (saved last
    // time the user configured columns, or merged in on an earlier run) can't outlive a rename at
    // the source of truth (e.g. a DB label edit).
    const rehydratedOrder = baseOrder.map(existing => ({
      ...existing,
      header: defaultColumns.find(col => col.field === existing.field)?.header ?? existing.header,
    }));

    columnOrder.value = [...rehydratedOrder, ...missingColumns];
    hasInitialized = true;
  };

  watch(() => toValue(options.defaultColumns), syncWithDefaults, { immediate: true });

  watch(() => options.ref.value?.d_columnOrder, () => {
    if (options.ref.value && !options.ref.value.d_columnOrder) {
      // eslint-disable-next-line camelcase
      options.ref.value.d_columnOrder = columnOrder.value.map(col => col.field);
    }
  });

  const handleVisibleColumnChange = (updatedColumns: string[]) => visibleColumns.value = updatedColumns;

  const handleColumnOrderChange = (updatedColumns: Column[]) => {
    columnOrder.value = updatedColumns;

    if (!options.ref.value?.d_columnOrder) return;
    // eslint-disable-next-line camelcase
    options.ref.value.d_columnOrder = updatedColumns.map(col => col.field);
  };

  return {
    columnOrder,
    handleColumnOrderChange,
    handleVisibleColumnChange,
    visibleColumns,
  };
};

export const useBuildDefaultColumns = (columns: Array<[string, string, boolean?]>): Column[] =>
  columns.map(column => ({
    field: column[0],
    header: column[1],
    visible: typeof column[2] === 'boolean' ? column[2] : true,
  }));
